import { API_URL } from '../config/api';
import { tokenManager } from '../context/AuthContext';
import { parseResponse, toQuery } from './expenseVouchersApi';

// API client for Expense Accounts processing and wallets (Phase 4).
// Every amount, status and permission is decided by the server; the UI only displays it.

const request = async (method, path, body) => parseResponse(await fetch(`${API_URL}${path}`, {
  method,
  headers: { ...tokenManager.getAuthHeader(), 'Content-Type': 'application/json' },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
}), 'You do not have Expense Accounts access.');

const ACC = '/expenses/accounts/vouchers';
const WAL = '/expenses/accounts/wallets';

export const PAYMENT_STATUS_META = {
  UNPROCESSED: { label: 'To book', color: 'default' },
  IN_PROCESS: { label: 'In Process', color: 'info' },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'warning' },
  PAID: { label: 'Paid', color: 'success' },
  ON_HOLD: { label: 'On Hold', color: 'secondary' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
};

export const WALLET_TYPE_LABELS = {
  ADVANCE: 'Advance',
  NEW_ADVANCE: 'New Advance',
  SETTLEMENT: 'Settlement',
  REIMBURSABLE: 'Reimbursable (wallet used / recovered)',
  ADD_BALANCE: 'Balance added',
  REVERSAL: 'Reversal (voucher cancelled)',
  OPENING_BALANCE: 'Opening balance (ERP migration)',
};

export const SETTLEMENT_RESULT_META = {
  BALANCE_TO_RECOVER: { label: 'Balance to Recover', severity: 'warning' },
  FULLY_SETTLED: { label: 'Fully Settled', severity: 'success' },
  ADDITIONAL_PAYABLE: { label: 'Additional Payable', severity: 'info' },
  CURRENCY_MISMATCH: { label: 'Currency mismatch', severity: 'error' },
};

// "INR 1,250.50" / "INR -2,000" (negative balances are shown as such)
export const formatMoney = (amount, currency) => {
  if (typeof amount !== 'number') return '-';
  const s = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  return currency ? `${currency} ${s}` : s;
};

// One id per Add Balance dialog, so a double submit never adds twice
export const newRequestId = () => `ab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// CSV is behind auth: fetch as a blob and hand it to the browser as a download
const downloadCsv = async (url, name) => {
  const response = await fetch(url, { headers: { ...tokenManager.getAuthHeader() } });
  if (!response.ok) await parseResponse(response, 'You do not have Expense Accounts access.');
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
};

export const accountsApi = {
  grid: (params) => request('GET', `${ACC}${toQuery(params)}`),
  exportUrl: (params) => `${API_URL}${ACC}/export${toQuery(params)}`,
  get: (id) => request('GET', `${ACC}/${id}`),
  book: (id, body) => request('PUT', `${ACC}/${id}/booking`, body),
  addPayment: (id, body) => request('POST', `${ACC}/${id}/payments`, body),
  markPaid: (id, paymentId, version) => request('POST', `${ACC}/${id}/payments/${paymentId}/mark-paid`, { version }),
  voidPayment: (id, paymentId, version, reason) => request('POST', `${ACC}/${id}/payments/${paymentId}/void`, { version, reason }),
  hold: (id, version, reason) => request('POST', `${ACC}/${id}/hold`, { version, reason }),
  release: (id, version, comment) => request('POST', `${ACC}/${id}/release`, { version, comment }),
  cancel: (id, version, reason) => request('POST', `${ACC}/${id}/cancel`, { version, reason }),

  exportCsv: (params) => downloadCsv(accountsApi.exportUrl(params), 'expense_accounts'),
};

// All Vouchers register (Phase 5A): read-only, every status. Line-level export = one row
// per expense line (erp_expense parity), same filters as the register.
const REG = '/expenses/accounts/register';
export const registerApi = {
  list: (params) => request('GET', `${REG}${toQuery(params)}`),
  exportLinesUrl: (params) => `${API_URL}${REG}/export-lines${toQuery(params)}`,
  exportLines: (params) => downloadCsv(registerApi.exportLinesUrl(params), 'expense_lines'),
};

export const walletApi = {
  me: (params) => request('GET', `/expenses/wallet/me${toQuery(params)}`),
  list: (params) => request('GET', `${WAL}${toQuery(params)}`),
  get: (employeeId, params) => request('GET', `${WAL}/${employeeId}${toQuery(params)}`),
  addBalance: (employeeId, body) => request('POST', `${WAL}/${employeeId}/add-balance`, body),
};

// Accounts reports and operational tools (Phase 5B-1). Read-only; every filter, total and
// export is computed on the server, money is per currency (never combined).
const REP = '/expenses/accounts/reports';
const BILLS = '/expenses/accounts/bills';

// "attachment; filename="x.zip"" -> x.zip
const filenameOf = (disposition, fallback) => {
  const m = /filename="?([^";]+)"?/i.exec(disposition || '');
  return m ? m[1] : fallback;
};

export const reportsApi = {
  dashboard: () => request('GET', `${REP}/dashboard`),
  payables: (params) => request('GET', `${REP}/payables${toQuery(params)}`),
  exportPayables: (params) => downloadCsv(`${API_URL}${REP}/payables/export${toQuery(params)}`, 'expense_payables'),
  payments: (params) => request('GET', `${REP}/payments${toQuery(params)}`),
  exportPayments: (params) => downloadCsv(`${API_URL}${REP}/payments/export${toQuery(params)}`, 'expense_payments'),
  walletBalances: (params) => request('GET', `${REP}/wallet-balances${toQuery(params)}`),
  exportWalletBalances: (params) => downloadCsv(`${API_URL}${REP}/wallet-balances/export${toQuery(params)}`, 'expense_wallet_balances'),
  walletLedger: (params) => request('GET', `${REP}/wallet-ledger${toQuery(params)}`),
  exportWalletLedger: (params) => downloadCsv(`${API_URL}${REP}/wallet-ledger/export${toQuery(params)}`, 'expense_wallet_ledger'),
  advances: (params) => request('GET', `${REP}/advances${toQuery(params)}`),
  exportAdvances: (params) => downloadCsv(`${API_URL}${REP}/advances/export${toQuery(params)}`, 'expense_advances_settlements'),

  // Bulk bill download: { voucherIds } or register filters
  billSummary: (body) => request('POST', `${BILLS}/summary`, body),
  // Downloads the ZIP and returns { downloaded, missing, failed, filename }
  downloadBills: async (body) => {
    const response = await fetch(`${API_URL}${BILLS}/download`, {
      method: 'POST',
      headers: { ...tokenManager.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) await parseResponse(response, 'You do not have Expense Accounts access.');
    const filename = filenameOf(response.headers.get('Content-Disposition'), 'Expense_Bills.zip');
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
    const n = (h) => parseInt(response.headers.get(h), 10) || 0;
    return { filename, downloaded: n('X-Bills-Downloaded'), missing: n('X-Bills-Missing'), failed: n('X-Bills-Failed') };
  },
};

export const AGEING_OPTIONS = [
  { value: '0_30', label: '0-30 days' },
  { value: '31_60', label: '31-60 days' },
  { value: '61_90', label: '61-90 days' },
  { value: '90_PLUS', label: 'Over 90 days' },
];

export const PAYMENT_ROW_STATUS_META = {
  PAID: { label: 'Paid', color: 'success' },
  PENDING: { label: 'Pending', color: 'default' },
  VOIDED: { label: 'Voided', color: 'error' },
};

export const ADVANCE_STATE_META = {
  UNSETTLED: { label: 'Unsettled', color: 'warning' },
  IN_SETTLEMENT: { label: 'Settlement in progress', color: 'info' },
  SETTLED: { label: 'Settled', color: 'success' },
  NOT_APPLICABLE: { label: 'Not applicable', color: 'default', hint: 'Draft, rejected or cancelled advances are never unsettled' },
};
